import StringUtils from './utils/StringUtils';
import MerideVideo from './utils/MerideVideo';
import { initMediaExplorer } from './utils/mediaExplorer';
import {
	adjustFontSize,
	applyFontSize,
	parseFontSizeFromElement,
	readStoredFontSize,
	syncZoomButtons,
} from './utils/editorFontZoom';

let offcanvasDetailInstance = null;

export default class OffcanvasDetail {
	entity = null;

	/** @type {number | null} Last index in the page list; only updated when opening content that is in the list. */
	#lastInPageIndex = null;

	/** @type {string | null} URL for the detail view. */
	detailUrl = null;

	/**
	 * One listener set per page. A later construct updates entity and detailUrl
	 * without calling init() again.
	 *
	 * app.js and page bundles each embed this module, so the instance is also
	 * kept on window and reused across those copies.
	 *
	 * @param {string|null} [entity]
	 * @param {string|null} [detailUrl]
	 */
	constructor(entity, detailUrl = null) {
		if (typeof window !== 'undefined' && window.offcanvasDetailInstance) {
			offcanvasDetailInstance = window.offcanvasDetailInstance;
		}

		if (offcanvasDetailInstance) {
			if (entity) {
				offcanvasDetailInstance.entity = entity;
			}
			if (detailUrl) {
				offcanvasDetailInstance.detailUrl = detailUrl;
			}

			return offcanvasDetailInstance;
		}

		this.entity = entity;
		this.detailUrl = detailUrl;
		offcanvasDetailInstance = this;

		if (typeof window !== 'undefined') {
			window.offcanvasDetailInstance = this;
		}

		this.init();
	}

	init() {
		this.offcanvas = $('#offcanvasDetail');
		this.contentDiv = $('#offcanvas-content');

		this.#setup();
	}

	#openOffcanvas(data, btn = null) {
		if (data.action === 'versions' || data.action === 'autosaves') {
			this.offcanvas = $('#offcanvasVersions');
			this.contentDiv = $('#offcanvas-versions-content');
		} else {
			this.offcanvas = $('#offcanvasDetail');
			this.contentDiv = $('#offcanvas-content');
		}

		if (!this.offcanvas.length) {
			console.error("offcanvas-right non trovato, impossibile aprire l'offcanvas");
			return;
		}
		if (!this.contentDiv.length) {
			console.error("offcanvas-content non trovato, impossibile aprire l'offcanvas");
			return;
		}

		const title = this.#resolveOffcanvasTitle(data);
		this.offcanvas.find('.offcanvas-title-static, [data-sources-picker-current-label]').text(title);
		// Il titolo lungo viene accorciato con i puntini: il testo intero resta nel suggerimento.
		this.offcanvas.find('.offcanvas-title-static').attr('title', title);

		if (data.action === 'children' && btn) {
			$('.offcanvas-pagination').show();
		} else {
			$('.offcanvas-pagination').hide();
		}

		if (data.action === 'children' && btn) {
			this.#drawDetails(btn);
		} else if (data.action === 'versions') {
			this.#drawVersions(data);
		} else if (data.action === 'autosaves') {
			this.#drawAutosaves(data);
		} else if (data.action === 'iframe') {
			this.#drawIframe(data);
		} else if (data.action !== 'empty') {
			console.error("Action non trovata, impossibile aprire l'offcanvas");
		}

		// Mostra offcanvas
		if (!this.offcanvas.hasClass('show')) {
			this.offcanvas.addClass('show');
		}
		this.contentDiv.attr('data-id', data.id);
		this.contentDiv.attr('data-params', JSON.stringify(data));

		// Update last in-page index only when the opened content is in the list (e.g. from a row/card button).
		if (data.action === 'children' && btn) {
			const buttons = this.#getAllChildButtons();
			const idx = buttons.findIndex((b) => b.getAttribute('data-id') === btn.attr('data-id'));
			if (idx >= 0) {
				this.#lastInPageIndex = idx;
			}
		}

		document.dispatchEvent(new CustomEvent('offcanvasopened'));
	}

	#resolveOffcanvasTitle(data) {
		if (data?.title) {
			return data.title;
		}

		if (data?.action !== 'iframe' || !data?.url) {
			return 'Dettaglio';
		}

		try {
			const url = new URL(data.url, window.location.origin);
			const normalized = `${url.pathname}${url.search}`.toLowerCase();

			if (normalized.includes('agenc')) {
				return 'Agenzie';
			}
			if (normalized.includes('collaborator')) {
				return 'Collaboratori';
			}
			if (normalized.includes('import') || normalized.includes('milenium')) {
				return 'Articoli Carta';
			}
			if (normalized.includes('archive')) {
				return 'Archivio';
			}
			if (normalized.includes('whatsapp')) {
				return 'WhatsApp';
			}
			if (normalized.includes('/ai') || normalized.includes('midjourney')) {
				return 'Immagini (AI)';
			}
		} catch (_error) {
			return 'Dettaglio';
		}

		return 'Dettaglio';
	}

	#closeOffcanvas() {
		if (this.offcanvas && this.offcanvas.hasClass('show')) {
			this.offcanvas.removeClass('show');
			this.contentDiv.find('[data-dismiss-translation]').off();
			this.contentDiv.find('[data-actbar="zoom"]').off('click.fontZoom');
			this.contentDiv.html('');

			this.#toggleIcon();
			this.setSourcesPickerDetachVisible(false);

			const $recordVersions = this.contentDiv.find('.record-version');
			if ($recordVersions.length > 0) {
				$recordVersions.off('click');
			}

			const translateButtons = this.contentDiv.find('[data-actbar="translate"]');
			if (translateButtons.length > 0) {
				translateButtons.off();
			}

			document.dispatchEvent(new CustomEvent('offcanvasclosed'));
		}
	}

	/**
	 * Show or hide the sources-picker detach control in the offcanvas header.
	 *
	 * @param {boolean} visible
	 * @param {string|null} [sessionId]
	 */
	setSourcesPickerDetachVisible(visible, sessionId = null) {
		if (!this.offcanvas?.length) {
			return;
		}

		const $btn = this.offcanvas.find('[data-sources-picker-detach]');
		if (!$btn.length) {
			return;
		}

		$btn.toggleClass('d-none', !visible);

		if (visible && sessionId) {
			this.offcanvas.attr('data-picker-session', sessionId);
			$btn.attr('data-picker-session', sessionId);
			return;
		}

		this.offcanvas.removeAttr('data-picker-session');
		$btn.removeAttr('data-picker-session');
	}

	#getButtonContainer(btn) {
		let container = btn.closest('tr');
		if (!container.length) {
			container = btn.closest('.card');
		}

		return container;
	}

	#drawDetails(btn) {
		this.#toggleIcon(btn);

		const popup = $('[name="popup"]').val();

		$.ajax({
			url: StringUtils.localizeUrl((this.detailUrl ?? window.appconfig.routes[this.entity].detail) + '/' + btn.attr('data-id') + (popup ? '?popup=true' : '')),
			type: 'GET',
			complete: (data) => {
				this.contentDiv.html(data.responseText);

				// Cerco il container padre (tr o card)
				const container = this.#getButtonContainer(btn);
				if (!container.length) {
					console.error("Container non trovato, impossibile trovare i contenuti per l'offcanvas");
					return;
				}

				// Clono il div .child
				// const $child = container.find('.child');
				// if ($child.length > 0) {
				this.contentDiv.replaceAll('data-original-title', 'title');

				this.#bindFontZoom();

				if (this.contentDiv.find('.offcanvas-translation').length > 0) {
					this.contentDiv.find('[data-actbar="translate"]').on('click', (e) => {
						const $button = $(e.currentTarget);
						const text = this.contentDiv.find('#editorjs').text();
						const lang = $button.attr('data-lang');
						StringUtils.translate(text, lang, (data) => {
							const newText = data.map((t) => t.text).join('');
							this.contentDiv.find('#translate-editorjs').text(newText);
							this.contentDiv.find('[data-copy]').attr('data-copy', newText);/*.on('click', () => {
								navigator.clipboard.writeText(newText);
								window.toastr.success('Testo copiato negli appunti');
							});*/
							const translationContainer = this.contentDiv.find('.offcanvas-translation-container');
							translationContainer.show();
							translationContainer.addClass('with-translation');
						}, null, null, $button);
					});
				}

				this.contentDiv.find('[data-dismiss-translation]').on('click', () => {
					const translationContainer = this.contentDiv.find('.offcanvas-translation-container');
					translationContainer.hide();
					translationContainer.removeClass('with-translation');
					this.contentDiv.find('#translate-editorjs').text('');
					this.contentDiv.find('[data-actbar="translate"].btn-primary').removeClass('btn-primary').addClass('btn-default');
				});

				this.contentDiv.find('.popup-preview-image').not('.media-explorer-gallery .media-explorer-slide').magnificPopup({
					type: 'image',
					closeOnContentClick: true,
					closeBtnInside: false,
					mainClass: 'mfp-with-zoom mfp-img-mobile',
					image: {
						verticalFit: true,
						titleSrc: (item) => item.el.attr('title') ?? '',
					},
					zoom: {
						enabled: true,
					},
					gallery: {
						enabled: true,
					},
				});

				this.contentDiv.find('.popup-preview-gallery').each((index, elem) => {
					$(elem).magnificPopup({
						delegate: '.popup-preview-gallery-image',
						type: 'image',
						closeOnContentClick: true,
						closeBtnInside: false,
						mainClass: 'mfp-with-zoom mfp-img-mobile',
						image: {
							verticalFit: true,
							titleSrc: (item) => item.el.attr('title') ?? '',
						},
						zoom: {
							enabled: true,
						},
						gallery: {
							enabled: true,
						},
					});
				});

				this.contentDiv.find('.popup-preview-video').each((index, elem) => {
					$(elem).magnificPopup({
						type: 'iframe',
						closeOnContentClick: false,
						closeBtnInside: false,
						mainClass: 'mfp-with-zoom mfp-img-mobile',
						zoom: {
							enabled: true,
						},
						gallery: {
							enabled: false,
						},
						callbacks: {
							elementParse: function (item) {
								const videoType = item.el.attr('data-video-type');
								if (videoType === 'video') {
									item.type = 'inline';
									const videoUrl = item.el.attr('href');
									item.src = `<div class="mfp-video-container" style="max-width: 900px; margin: 0 auto; background: #000;">
										<video controls autoplay style="width: 100%; max-height: 80vh;">
											<source src="${videoUrl}" type="video/mp4">
											Il tuo browser non supporta il tag video.
										</video>
									</div>`;
								} else if (videoType === 'meride') {
									item.type = 'inline';
									const customer = item.el.attr('data-meride-customer') || window.merideCustomerId;
									const code = item.el.attr('data-meride-code');
									const { html, containerId } = MerideVideo.createPopupContainer(customer, code);
									item.src = html;
									MerideVideo.initPopupPlayer(containerId, customer, code);
								}
							},
							close: function () {
								const video = document.querySelector('.mfp-video-container video');
								if (video) {
									video.pause();
									video.src = '';
								}
								MerideVideo.cleanupPopupPlayers();
							},
						},
					});
				});

				initMediaExplorer(this.contentDiv[0]);

				const bookmarks = JSON.parse(sessionStorage.getItem('naxos-bookmarks')) || {};
				const bookmarkEntry = bookmarks[window.location.pathname];
				const bookmarkedId = bookmarkEntry?.match(/bookmark_id=(\d+)/)?.[1];
				const buttonId = String(btn.attr('data-id'));

				if (bookmarkedId && bookmarkedId === buttonId) {
					this.contentDiv.addClass('bookmarked');
				} else {
					this.contentDiv.removeClass('bookmarked');
				}

				if (typeof window.syncBookmarkLabels === 'function') {
					window.syncBookmarkLabels(bookmarkedId || null);
				}

				// rattoppo per non far vedere le immagini doppia sia nella preview che nell'elenco dei componenti multimedia
				const cover = $('#offcanvas-content .offcanvas-images a').first().attr('href');
				if (cover) {
					$('#offcanvas-content .multimedia-container a[href*="' + cover + '"]').hide();
				}
				// }
			},
		});
	}

	#drawVersions(data) {
		// const entity = this.getAttribute('data-entity');
		// const id = this.getAttribute('data-id');
		if (!data.entity || !data.id) {
			if (!data.entity) {
				console.error("Entity non trovato, impossibile trovare i dati per l'offcanvas");
			}
			if (!data.id) {
				console.error("ID non trovato, impossibile trovare i dati per l'offcanvas");
			}
			return;
		}

		this.#loadVersionPage(data.id, data.entity, 1);
	}

	#drawIframe(data) {
		// const url = this.getAttribute('data-url');
		if (!data.url) {
			console.error("URL non trovato, impossibile caricare l'iframe");
			return;
		}

		let loadingSpinner = document.getElementById('offcanvas-loading-spinner');
		if (!loadingSpinner) {
			loadingSpinner = document.createElement('div');
			loadingSpinner.id = 'offcanvas-loading-spinner';
			loadingSpinner.classList.add('align-items-center', 'justify-content-center', 'w-full', 'h-full');
			loadingSpinner.style.display = 'flex';
			loadingSpinner.style.position = 'absolute';
			loadingSpinner.style.top = '50%';
			loadingSpinner.style.left = '50%';
			loadingSpinner.style.transform = 'translate(-50%, -50%)';
			loadingSpinner.innerHTML = `<i class="icon fa-solid fa-spinner fa-spin font-size-50 grey-400"></i>`;
			this.contentDiv.append(loadingSpinner);
		} else {
			loadingSpinner.style.display = 'flex';
		}

		let iframe = document.querySelector('.offcanvas-iframe');
		if (!iframe) {
			iframe = document.createElement('iframe');
			iframe.classList.add('offcanvas-iframe');
			iframe.src = data.url;
			iframe.width = '100%';
			iframe.height = '100%';
			iframe.addEventListener('load', function () {
				// Nascondi il loading e mostra l'iframe
				loadingSpinner.style.display = 'none';
				iframe.style.display = 'block';
			});
			this.contentDiv.append(iframe);
		}
	}

	#loadVersionPage(id, entity, page = 1) {
		const url = StringUtils.localizeUrl(
			window.appconfig.routes.versions + '/' + entity + '/' + id + '?page=' + page,
		);

		$.ajax({
			url: url,
			type: 'GET',
			success: (data) => {
				const content = data.data;

				if (!content.length) {
					this.contentDiv.html('<p class="text-muted p-2">Nessuna versione.</p>');
					return;
				}

				const pagination = {
					current_page: data.current_page,
					last_page: data.last_page,
					total: data.total,
				};

				let stringified = content
					.map((version, index) => {
						const userName = version.user && version.user.name ? version.user.name : 'Sistema';
						const versionDate = new Date(version.updated_at).toLocaleDateString() + '&nbsp;&nbsp;-&nbsp;&nbsp;' + new Date(version.updated_at).toLocaleTimeString();
						return `<div class="record-version" data-id="${version.id}">
						<h6><i class="fa fa-chevron-right"></i> Versione ${pagination.total - index}&nbsp;&nbsp;-&nbsp;&nbsp;${versionDate}<span class="float-right">${userName}</span></h6>
						<div class="record-version-content border rounded p-2" style="display:none; max-height: 500px; overflow: auto;">
							<table>${this.#parseJson(version.contents)}</table>
						</div>
					</div>`;
					})
					.join('');
				stringified += `<ul class="record-version-pagination pagination float-right">
					<li class="page-item ${pagination.current_page === 1 ? 'disabled' : ''}" data-action="versions" data-id="${id}" data-entity="${entity}" data-page="${
						pagination.current_page - 1
					}">
						<span class="page-link" aria-hidden="true">‹</span>
					</li>
					<li class="page-item ${
						pagination.current_page === pagination.last_page ? 'disabled' : ''
					}" data-action="versions" data-id="${id}" data-entity="${entity}" data-page="${pagination.current_page + 1}">
						<span class="page-link" aria-hidden="true">›</span>
					</li>
				</ul>`;

				this.contentDiv.html(stringified);

				$('.record-version').on('click', function () {
					const $this = $(this);
					const $table = $this.find('.record-version-content');
					if ($table.is(':visible')) {
						$table.hide();
					} else {
						$table.show();
					}

					$this.find('h6 i').toggleClass(['fa-chevron-right', 'fa-chevron-down']);
				});
			},
			error: (error) => {
				console.error(error);
				this.contentDiv.html('<p class="text-danger p-2">Impossibile caricare lo storico.</p>');
			},
		});
	}

	#drawAutosaves(data) {
		if (!data.entity || !data.id) {
			if (!data.entity) {
				console.error("Entity non trovato, impossibile trovare gli autosave per l'offcanvas");
			}
			if (!data.id) {
				console.error("ID non trovato, impossibile trovare gli autosave per l'offcanvas");
			}
			return;
		}

		this.#loadAutosaves(data.id, data.entity, 1);
	}

	#loadAutosaves(id, entity, page = 1) {
		const url = window.appconfig.routes[entity].record_autosaves + '/' + id + '?page=' + page;

		$.ajax({
			url: url,
			type: 'GET',
			success: (data) => {
				const content = data.data;
				const pagination = {
					current_page: data.current_page,
					last_page: data.last_page,
					total: data.total,
				};
				const editBase = window.appconfig.routes[entity].edit;

				let stringified = '';

				if (!content.length) {
					stringified = '<p class="text-muted p-2">Nessun salvataggio automatico per questo record.</p>';
					this.contentDiv.html(stringified);
					return;
				}

				stringified = content
					.map((autosave) => {
						const created = new Date(autosave.created_at);
						const applyUrl = editBase + '/' + id + '?fastops=autosave&autosave=' + autosave.id;
						return `<div class="record-version">
						<h6>
							<i class="fa fa-clock-rotate-left"></i> Salvataggio del ${created.toLocaleDateString()} ${created.toLocaleTimeString()}
							${autosave.owner ? '<span class="float-right">' + autosave.owner + '</span>' : ''}
						</h6>
						<div class="border rounded p-2 d-flex justify-content-between align-items-center gap-2">
							<span>${autosave.title ? autosave.title : '<span class="text-muted">(senza titolo)</span>'}</span>
							<button type="button" class="btn btn-primary btn-sm apply-autosave text-nowrap" data-url="${applyUrl}">
								<i class="fa fa-rotate-left mr-1"></i> Applica
							</button>
						</div>
					</div>`;
					})
					.join('');

				stringified += `<ul class="record-version-pagination pagination float-right">
					<li class="page-item ${pagination.current_page === 1 ? 'disabled' : ''}" data-action="autosaves" data-id="${id}" data-entity="${entity}" data-page="${
						pagination.current_page - 1
					}">
						<span class="page-link" aria-hidden="true">‹</span>
					</li>
					<li class="page-item ${
						pagination.current_page === pagination.last_page ? 'disabled' : ''
					}" data-action="autosaves" data-id="${id}" data-entity="${entity}" data-page="${pagination.current_page + 1}">
						<span class="page-link" aria-hidden="true">›</span>
					</li>
				</ul>`;

				this.contentDiv.html(stringified);

				this.contentDiv.find('.apply-autosave').on('click', function () {
					// Keep the user on the same tab after the reload (read by FormTabs); pass locale to skip the locale redirect.
					const activeTab = $('#additional-contents [data-manage].btn-primary').first().attr('data-manage');
					const locale = new URLSearchParams(window.location.search).get('locale');
					const applyUrl =
						$(this).attr('data-url') +
						(activeTab ? '&tab=' + encodeURIComponent(activeTab) : '') +
						(locale ? '&locale=' + encodeURIComponent(locale) : '');
					bootbox.confirm({
						title: 'Applica salvataggio automatico',
						message: 'Applicando questo salvataggio le modifiche non salvate andranno perse. Continuare?',
						buttons: {
							confirm: { label: 'Applica', className: 'btn-primary' },
							cancel: { label: 'Annulla', className: 'btn-default' },
						},
						callback: (result) => {
							if (result) {
								window.form?.clearConfirmPageLeave?.();
								window.location.href = applyUrl;
							}
						},
					});
				});

				this.contentDiv.find('.record-version-pagination .page-item:not(.disabled)').on('click', (e) => {
					const $li = $(e.currentTarget);
					this.#loadAutosaves($li.attr('data-id'), $li.attr('data-entity'), parseInt($li.attr('data-page'), 10));
				});
			},
			error: function (error) {
				console.error(error);
			},
		});
	}

	#parseJson(json, level = 0) {
		let accumulator = '';
		if (json === null) return '';

		Object.keys(json).forEach((key) => {
			const spaces = '&nbsp;'.repeat(level * 4);
			const tdClasses = 'align-top p-1';
			if (
				(typeof json[key] === 'string' &&
					((json[key].startsWith('{') && json[key].endsWith('}')) || (json[key].startsWith('[') && json[key].endsWith(']')))) ||
				(typeof json[key] === 'object' && json[key] !== null)
			) {
				accumulator += `<tr><td class="${tdClasses}">${spaces}<b>${key}</b></td><td class="${tdClasses}"></td></tr>`;
				const block = typeof json[key] === 'object' ? json[key] : JSON.parse(json[key]);
				accumulator += this.#parseJson(block, level + 1);
			} else {
				accumulator += `<tr><td class="${tdClasses}">${spaces}<b>${key}</b></td><td class="${tdClasses}">${json[key]}</td></tr>`;
			}
		});

		return accumulator;
	}

	#setup() {
		$('[data-toggle="offcanvas"]').on('click', (e) => {
			const $btn = $(e.currentTarget);
			if (!$btn.find('i.fa-circle-minus').length) {
				const data = {
					action: $btn.attr('data-action'),
					id: $btn.attr('data-id'),
					entity: $btn.attr('data-entity'),
					url: $btn.attr('data-url'),
					title: $btn.attr('title'),
				};
				document.dispatchEvent(new CustomEvent('openOffcanvas', { detail: { data, btn: $btn } }));
			} else {
				$('[data-dismiss-offcanvas]').trigger('click');
			}
		});

		// Apri offcanvas da eventi
		document.addEventListener('openOffcanvas', (event) => {
			this.#openOffcanvas(event.detail.data, event.detail.btn);
		});

		// Chiudi con ESC
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.offcanvas) {
				this.#closeOffcanvas();
			}
		});

		document.addEventListener('closeOffcanvas', () => {
			this.#closeOffcanvas();
		});

		window.addEventListener('message', (event) => {
			if (event.data?.action === 'set-title' && event.data.title && this.offcanvas?.hasClass('show')) {
				this.offcanvas.find('.offcanvas-title-static, [data-sources-picker-current-label]').text(event.data.title);
			}
		});

		// Chiudi pulsante
		$('[data-dismiss-offcanvas]').on('click', (e) => {
			this.#closeOffcanvas();
		});

		$('.offcanvas-header .open-previous').on('click', () => {
			const buttons = this.#getAllChildButtons();
			let index = buttons.findIndex((btn) => btn.getAttribute('data-id') === this.contentDiv.attr('data-id'));
			if (index === -1) {
				index = this.#lastInPageIndex;
			}
			if (index != null && index > 0) {
				buttons[index - 1].click();
			}
		});

		$('.offcanvas-header .open-next').on('click', () => {
			const buttons = this.#getAllChildButtons();
			let index = buttons.findIndex((btn) => btn.getAttribute('data-id') === this.contentDiv.attr('data-id'));
			if (index === -1) {
				index = this.#lastInPageIndex;
			}
			if (index != null && index < buttons.length - 1) {
				buttons[index + 1].click();
			}
		});

		// $('[data-action="children"]').each((index, btn) => {
		// 	const $btn = $(btn);
		// 	$btn.on('click', () => {
		// 		if (this.offcanvas.find('i.fa-circle-minus').length > 0) {
		// 			$('[data-dismiss-offcanvas]').each((index, btn) => {
		// 				$btn.trigger('click');
		// 			});
		// 		}

		// 		const $button = this.offcanvas.find(btn);
		// 		this.#toggleIcon($button);
		// 	});
		// });

	}

	#bindFontZoom() {
		const $editors = this.contentDiv.find('#editorjs, #translate-editorjs');
		if (!$editors.filter('#editorjs').length) {
			return;
		}

		const storedFontSize = readStoredFontSize();
		if (storedFontSize !== null) {
			applyFontSize($editors, storedFontSize);
		}
		syncZoomButtons(
			this.contentDiv,
			storedFontSize ?? parseFontSizeFromElement($editors.filter('#editorjs')) ?? 16,
		);

		this.contentDiv
			.find('[data-actbar="zoom"]')
			.off('click.fontZoom')
			.on('click.fontZoom', (e) => {
				const type = $(e.currentTarget).attr('data-actype');
				adjustFontSize(this.contentDiv.find('#editorjs, #translate-editorjs'), type, this.contentDiv);
			});
	}

	#toggleIcon(button = null) {
		// resetto altri eventuali bototni attivi
		const buttons = button
			? $('[data-toggle="offcanvas"]:not([data-id="' + button.attr('data-id') + '"]) i.fa-circle-minus')
			: $('[data-toggle="offcanvas"] i.fa-circle-minus');
		buttons.removeClass('fa-circle-minus').addClass('fa-circle-plus');

		if (button) {
			// attibo o disattivo quello corrente
			const $i = button.find('i');
			if ($i.hasClass('fa-circle-plus')) {
				$i.removeClass('fa-circle-plus').addClass('fa-circle-minus');
			} else {
				$i.removeClass('fa-circle-minus').addClass('fa-circle-plus');
			}
		}
	}

	// Una voce per contenuto: una riga può avere più elementi che aprono lo stesso dettaglio (es. pulsante e miniatura),
	// altrimenti Precedente/Successivo passerebbero dall'uno all'altro restando sullo stesso contenuto.
	#getAllChildButtons() {
		const seen = new Set();

		return $('[data-toggle="offcanvas"][data-action="children"]')
			.toArray()
			.filter((btn) => {
				const id = btn.getAttribute('data-id');
				if (seen.has(id)) {
					return false;
				}
				seen.add(id);
				return true;
			});
	}
}
